import { useRef, useState } from 'react';
import { Download, Upload, FileSpreadsheet, X, Trash2 } from 'lucide-react';
import Button from '../common/Button.js';
import Alert from '../common/Alert.js';
import routeService from '../../services/RouteService.js';
import { downloadBlob } from '../../utils/downloadBlob.js';

const hubLabel = (hub) => {
  if (!hub) return '';
  if (typeof hub === 'string') return hub;
  return (
    hub.display_name
    || hub.location
    || hub.parent_hub_name
    || ''
  );
};

const locationNames = (hubs) =>
  (hubs || []).map(hubLabel).filter(Boolean).join(', ') || '—';

const sourceTargetLabel = (route) => {
  if (!route) return '—';
  if (route.source_target_summary) return route.source_target_summary;
  if (route.source_label || route.target_label) {
    return `${route.source_label || '—'} → ${route.target_label || '—'}`;
  }
  const from = route.from_location ? hubLabel(route.from_location) : '';
  const to = route.to_location ? hubLabel(route.to_location) : '';
  if (from || to) return `${from || '—'} → ${to || '—'}`;
  return `${locationNames(route.source_locations)} → ${locationNames(route.target_locations)}`;
};

const crossingSummary = (points) => {
  if (!points?.length) return '—';
  return points
    .map((point) => {
      const place = point.location?.location || 'Crossing';
      const branches = (point.branches || []).map((hub) => hub.location).filter(Boolean).join(', ');
      return branches ? `${place}: ${branches}` : place;
    })
    .join(' · ');
};

const DispatchExcelModal = ({ open, onClose, onCreated }) => {
  const inputRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rows, setRows] = useState(null);

  if (!open) return null;

  const invalidCount = (rows || []).filter((row) => !row.valid).length;
  const validRows = (rows || []).filter((row) => row.valid);

  const resetAndClose = () => {
    setRows(null);
    onClose();
  };

  const downloadTemplate = async () => {
    try {
      setDownloading(true);
      const res = await routeService.downloadDispatchTemplate();
      await downloadBlob(
        res.data,
        'daily_fleet_trip_dispatch_log.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
      Alert.success('Dispatch template downloaded');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!String(file.name).toLowerCase().endsWith('.xlsx')) {
      Alert.error('Upload a .xlsx file from the template');
      return;
    }
    try {
      setUploading(true);
      const res = await routeService.previewDispatch(file);
      const preview = res.data?.data || res.data;
      setRows(preview?.rows || []);
      if (!preview?.rows?.length) {
        Alert.error('No rows found in the sheet');
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not read the Excel file');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeRow = (rowNumber) => {
    setRows((prev) => (prev || []).filter((row) => row.row !== rowNumber));
  };

  const confirm = async () => {
    if (!validRows.length || invalidCount) return;
    try {
      setConfirming(true);
      const payload = validRows.map((row) => ({
        row: row.row,
        date_of_journey: row.date_of_journey,
        service_number: row.service_number,
        vehicle_number: row.vehicle_number,
        driver_name: row.driver_name,
        driver_mobile: row.driver_mobile,
      }));
      const res = await routeService.confirmDispatch(payload);
      const created = res.data?.data || res.data || [];
      const ids = (Array.isArray(created) ? created : []).map((item) => item.ml_sheet_id).filter(Boolean);
      Alert.success(
        ids.length
          ? `Created ${ids.length} ML sheet${ids.length === 1 ? '' : 's'}: ${ids.join(', ')}`
          : (res.message || 'ML sheets created')
      );
      setRows(null);
      if (onCreated) onCreated(created);
      onClose();
    } catch (err) {
      const failed = err.response?.data?.failed;
      if (Array.isArray(failed) && failed.length) {
        Alert.error(failed.map((item) => `Row ${item.row}: ${(item.errors || []).join(', ')}`).join(' · '));
      } else {
        Alert.error(err.response?.data?.error || 'Could not create ML sheets');
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-3 sm:p-4"
      onClick={resetAndClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Daily Fleet Dispatch
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Download a fresh template. Journey date is DD-MM-YYYY. Type the driver mobile as the full number, then review before ML sheets are created.
            </p>
          </div>
          <button type="button" onClick={resetAndClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 flex flex-col sm:flex-row gap-2 border-b border-gray-100">
          <Button type="button" variant="secondary" icon={Download} loading={downloading} onClick={downloadTemplate}>
            Download template
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          <Button type="button" variant="primary" icon={Upload} loading={uploading} onClick={() => inputRef.current?.click()}>
            Upload filled Excel
          </Button>
        </div>

        <div className="flex-1 overflow-auto px-4 py-3">
          {!rows ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              Upload a filled sheet to review every trip before creating ML sheets.
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">All rows were removed.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-600">
                    <th className="px-2 py-2">Date</th>
                    <th className="px-2 py-2">Service</th>
                    <th className="px-2 py-2">Source → Target</th>
                    <th className="px-2 py-2">Time</th>
                    <th className="px-2 py-2">Vehicle</th>
                    <th className="px-2 py-2">Driver</th>
                    <th className="px-2 py-2">Crossing</th>
                    <th className="px-2 py-2">Check</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.row} className={`border-t border-gray-100 ${row.valid ? '' : 'bg-red-50'}`}>
                      <td className="px-2 py-2 whitespace-nowrap">{row.date_of_journey || '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap font-medium">{row.service_number || '—'}</td>
                      <td className="px-2 py-2 text-xs sm:text-sm">
                        {sourceTargetLabel(row.route)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {row.route?.departure_time || row.route?.arrival_time
                          ? `${row.route.departure_time || '—'} – ${row.route.arrival_time || '—'}`
                          : '—'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">{row.vehicle_number || '—'}</td>
                      <td className="px-2 py-2">
                        <div>{row.driver_name || '—'}</div>
                        <div className="text-xs text-gray-500">{row.driver_mobile}</div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-700">{crossingSummary(row.route?.crossing_points)}</td>
                      <td className="px-2 py-2 text-xs">
                        {row.valid ? (
                          <span className="text-emerald-700 font-medium">Ready</span>
                        ) : (
                          <span className="text-red-700">{(row.errors || []).join('; ')}</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => removeRow(row.row)}
                          className="p-1.5 rounded-lg text-red-600 hover:bg-red-100"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-xs text-gray-500">
            {rows
              ? `${validRows.length} ready${invalidCount ? `, ${invalidCount} need a fix or removal` : ''}`
              : 'Source, target, times, and crossing come from the matching service number.'}
          </p>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="secondary" onClick={resetAndClose}>Close</Button>
            <Button
              type="button"
              variant="primary"
              loading={confirming}
              disabled={!validRows.length || invalidCount > 0}
              onClick={confirm}
            >
              Create ML sheets
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DispatchExcelModal;
